import React, { forwardRef, useState } from 'react';
import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useDroppable,
    useSensor,
    useSensors,
    rectIntersection,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const LIST_PREFIX = 'list-';

// Native form controls are never drag handles. Safari opens their popups (ex.
//  a <select> menu) on pointerdown and then stops delivering pointerup and
//  pointercancel to the document, so a drag activated underneath an open popup
//  never ends and the item stays stuck to the cursor until the page is
//  refreshed. Chrome cancels the drag for us, which is why this only showed up
//  in Safari. Items spread the sortable listeners across their whole row, so
//  the guard has to live in the sensors.
const NON_DRAGGABLE_SELECTOR =
    'input, select, textarea, option, optgroup, button, a, [contenteditable="true"], [data-no-dnd="true"]';

function startsOnFormControl(target) {
    return (
        target instanceof Element && !!target.closest(NON_DRAGGABLE_SELECTOR)
    );
}

class ItemPointerSensor extends PointerSensor {
    static activators = [
        {
            eventName: 'onPointerDown',
            handler: ({ nativeEvent: event }, { onActivation }) => {
                if (!event.isPrimary || event.button !== 0) {
                    return false;
                }

                if (startsOnFormControl(event.target)) {
                    return false;
                }

                onActivation?.({ event });
                return true;
            },
        },
    ];
}

class ItemKeyboardSensor extends KeyboardSensor {
    static activators = [
        {
            eventName: 'onKeyDown',
            handler: (event, options, context) => {
                // Otherwise a space bar press inside a field bubbles up to the
                //  row and starts a keyboard drag instead of typing a space
                if (startsOnFormControl(event.target)) {
                    return false;
                }

                return KeyboardSensor.activators[0].handler(
                    event,
                    options,
                    context,
                );
            },
        },
    ];
}

const Item = forwardRef(({ renderItem, isActive, item, ...props }, ref) => {
    return (
        <div {...props} ref={ref}>
            {renderItem({ item, isActive })}
        </div>
    );
});

function SortableItem(props) {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id: props.item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Item
            ref={setNodeRef}
            item={props.item}
            isActive={props.isActive}
            renderItem={props.renderItem}
            style={style}
            {...attributes}
            {...listeners}
        />
    );
}

const SortableList = ({
    items,
    listId,
    renderItem,
    Container,
    activeId,
    idx,
}) => {
    const { setNodeRef, isOver } = useDroppable({
        id: `${LIST_PREFIX}${listId}`,
        disabled: items.length > 0,
    });

    return (
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <div ref={setNodeRef}>
                {' '}
                {/*This is used for empty list dropping*/}
                <Container listId={listId} idx={idx}>
                    {items.map(item => (
                        <SortableItem
                            key={item.id}
                            item={item}
                            isActive={item.id === activeId}
                            renderItem={renderItem}
                        />
                    ))}
                </Container>
            </div>
        </SortableContext>
    );
};

const SortableContainers = ({ items, setItems, renderItem, Container }) => {
    const [activeId, setActiveId] = useState(null);
    const sensors = useSensors(
        useSensor(ItemPointerSensor, {
            activationConstraint: {
                distance: 10,
                delay: 500,
            },
        }),
        useSensor(ItemKeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
            activationConstraint: {
                tolerance: 5,
            },
        }),
    );

    function handleDragStart(event) {
        const { active } = event;

        setActiveId(active.id);
    }

    function handleDragEnd(event) {
        const { active, over } = event;

        if (active && over && active.id !== over.id) {
            const containerId = findItemContainer(active.id);

            let newList = [...items[containerId]];
            const oldIndex = indexOfId(newList, active.id);
            const newIndex = indexOfId(newList, over.id);

            newList = arrayMove(newList, oldIndex, newIndex);

            setItems({
                ...items,
                [containerId]: newList,
            });
        }

        setActiveId(null);
    }

    function findItemContainer(id) {
        if (typeof id.indexOf === 'function' && id.indexOf(LIST_PREFIX) > -1) {
            return id.replace(LIST_PREFIX, '');
        }

        for (const listId in items) {
            if (indexOfId(items[listId], id) > -1) {
                return listId;
            }
        }

        return false;
    }

    function indexOfId(list, id) {
        for (const i in list) {
            if (list[i].id === id) {
                return i;
            }
        }

        return -1;
    }

    function findItemById(id) {
        for (const listId in items) {
            const idx = indexOfId(items[listId], id);
            if (idx > -1) {
                return items[listId][idx];
            }
        }

        return false;
    }

    function handleDragOver(event) {
        const { active, over } = event;

        // Find the containers for over/active
        //  if different, then remove active from the old container
        //  move active to the `over` container
        //  put in the right position in the new list
        if (!over) {
            console.log('null over in', event);
            return;
        }

        const activeContainer = findItemContainer(active.id);
        const overContainer = findItemContainer(over.id);

        if (overContainer === activeContainer) {
            return;
        }

        const newIndex = Math.max(indexOfId(items[overContainer], over.id), 0);
        const activeIndex = indexOfId(items[activeContainer], active.id);

        // console.log('moved over active => over', JSON.stringify(activeContainer), JSON.stringify(overContainer))
        // console.log('activeIndex => newIndex', activeIndex, newIndex, items[activeContainer][activeIndex])
        // console.log('overContainer items', items[overContainer], items)

        if (typeof items[activeContainer] === 'undefined') {
            return;
        }

        // Different containers...
        setItems({
            ...items,
            [activeContainer]: items[activeContainer].filter(
                item => item.id !== active.id,
            ),
            [overContainer]: [
                ...items[overContainer].slice(0, newIndex),
                items[activeContainer][activeIndex],
                ...items[overContainer].slice(
                    newIndex,
                    items[overContainer].length,
                ),
            ],
        });
    }

    const fixCursorSnapOffset = args => {
        // Bail out if keyboard activated
        if (!args.pointerCoordinates) {
            return rectIntersection(args);
        }
        const { x, y } = args.pointerCoordinates;
        const { width, height } = args.collisionRect;
        const updated = {
            ...args,
            // The collision rectangle is broken when using snapCenterToCursor. Reset
            // the collision rectangle based on pointer location and overlay size.
            collisionRect: {
                width,
                height,
                bottom: y + height / 2,
                left: x - width / 2,
                right: x + width / 2,
                top: y - height / 2,
            },
        };
        return rectIntersection(updated);
    };

    return (
        <DndContext
            sensors={sensors}
            // collisionDetection={closestCenter}
            collisionDetection={fixCursorSnapOffset}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
        >
            {Object.keys(items).map((key, idx) => (
                <SortableList
                    key={key}
                    items={items[key]}
                    listId={key}
                    renderItem={renderItem}
                    Container={Container}
                    idx={idx}
                    activeId={activeId}
                />
            ))}

            <DragOverlay
                collisionDetection={fixCursorSnapOffset}
                // modifiers={[snapCenterToCursor]}
                // ^^^ This was to fix some drag and drop, but seems to be working OK w/o? Not having this makes the snap when clicking the left drag handle a little nicer
            >
                {activeId ? (
                    <div className="bg-white">
                        <div className="opacity-50">
                            <Item
                                item={findItemById(activeId)}
                                renderItem={renderItem}
                            />
                        </div>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

export default SortableContainers;
